import { JSX, ReactNode, forwardRef } from 'react';
import cn from 'classnames';

import styles from './grid.module.scss';
import { generateResponsiveClasses, isResponsiveObject, buildResponsiveStyles, TResponsiveValue } from '../shared/responsive-classes';
import { processInlineStyles } from '../shared/style-processors';

export type TGridAutoFlow = 'row' | 'column' | 'dense' | 'row dense' | 'column dense';
export type TGridTemplate = string | number | 'none' | 'subgrid';

export type { TResponsiveValue };

export interface IGridProps {
  children?: ReactNode,
  className?: string,
  templateColumns?: TResponsiveValue<string | number>,
  templateRows?: TResponsiveValue<string | number>,
  gap?: TResponsiveValue<number> | TResponsiveValue<string>,
  dataTest?: string,
  as?: keyof JSX.IntrinsicElements,
  style?: React.CSSProperties,
}

interface INonResponsiveGridProps {
  gap?: TResponsiveValue<number> | TResponsiveValue<string>,
  templateColumns?: string | number,
  templateRows?: string | number,
}

const Grid = forwardRef<HTMLDivElement, IGridProps>(({
  children,
  className,
  templateColumns,
  templateRows,
  gap,
  dataTest = 'grid',
  as: Component = 'div' as any,
  style,
  ...props
}, ref) => {
  const hasResponsiveColumns = isResponsiveObject(templateColumns);
  const hasResponsiveRows = isResponsiveObject(templateRows);

  const gridClasses = cn(
    styles.grid,
    generateResponsiveClasses(gap, 'gap', styles),
    hasResponsiveColumns && styles.responsiveTemplateColumns,
    hasResponsiveRows && styles.responsiveTemplateRows,
    className,
  );

  // For non-responsive values, use existing inline style processing
  const nonResponsiveProps: INonResponsiveGridProps = { gap };
  if (!hasResponsiveColumns) nonResponsiveProps.templateColumns = templateColumns;
  if (!hasResponsiveRows) nonResponsiveProps.templateRows = templateRows;

  const inlineStyles = processInlineStyles(nonResponsiveProps, styles);

  // For responsive values, add CSS custom properties
  const responsiveVars = {
    ...buildResponsiveStyles(templateColumns, 'grid-tpl-cols'),
    ...buildResponsiveStyles(templateRows, 'grid-tpl-rows'),
  };

  const combinedStyles = { ...inlineStyles, ...responsiveVars, ...style };

  return (
    <Component
      ref={ ref }
      className={ gridClasses }
      data-test={ dataTest }
      style={ combinedStyles }
      { ...props }
    >
      { children }
    </Component>
  );
});

export default Grid;
